import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FORMS_CONFIG } from '../forms-config.token';

export interface Workspace {
  id: string;
  name: string;
}

export interface SentryUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface SyncedWorkspace {
  id: string;
  name: string | null;
  code: string | null;
  organizationId: string | null;
}

const STORAGE_WORKSPACE = 'sentry_active_workspace';
const STORAGE_USER = 'sentry_user';
const STORAGE_WORKSPACES = 'sentry_workspaces';

@Injectable({ providedIn: 'root' })
export class WorkspaceService {
  private http = inject(HttpClient);

  private _activeWorkspace = signal<Workspace | null>(null);
  private _currentUser = signal<SentryUser | null>(null);
  private _workspaces = signal<SyncedWorkspace[]>([]);

  readonly activeWorkspace = this._activeWorkspace.asReadonly();
  readonly currentUser = this._currentUser.asReadonly();
  readonly workspaces = this._workspaces.asReadonly();
  readonly workspaceCount = () => this._workspaces().length;

  constructor() {
    const ws = localStorage.getItem(STORAGE_WORKSPACE);
    const user = localStorage.getItem(STORAGE_USER);
    const wsl = localStorage.getItem(STORAGE_WORKSPACES);
    if (ws) this._activeWorkspace.set(JSON.parse(ws));
    if (user) this._currentUser.set(JSON.parse(user));
    if (wsl) this._workspaces.set(JSON.parse(wsl));
  }

  cacheFromSync(workspaces: SyncedWorkspace[], profile: SentryUser): void {
    this._workspaces.set(workspaces);
    this._currentUser.set(profile);
    localStorage.setItem(STORAGE_WORKSPACES, JSON.stringify(workspaces));
    localStorage.setItem(STORAGE_USER, JSON.stringify(profile));
  }

  loadWorkspaces(): Observable<SyncedWorkspace[]> {
    const config = inject(FORMS_CONFIG);
    return this.http
      .get<SyncedWorkspace[]>(`${config.apiUrl}/api/workspaces`)
      .pipe(
        tap((list) => {
          this._workspaces.set(list);
          localStorage.setItem(STORAGE_WORKSPACES, JSON.stringify(list));
        })
      );
  }

  setActiveWorkspace(workspace: Workspace): void {
    this._activeWorkspace.set(workspace);
    localStorage.setItem(STORAGE_WORKSPACE, JSON.stringify(workspace));
  }

  /** Sets the active workspace directly from an external prop (Angular Elements usage). */
  setFromProps(workspace: Workspace): void {
    this._activeWorkspace.set(workspace);
  }

  /** Only clears the selected workspace — keeps user profile and workspace list intact. */
  clearActiveWorkspace(): void {
    this._activeWorkspace.set(null);
    localStorage.removeItem(STORAGE_WORKSPACE);
  }

  clearSession(): void {
    this._activeWorkspace.set(null);
    this._currentUser.set(null);
    this._workspaces.set([]);
    localStorage.removeItem(STORAGE_WORKSPACE);
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_WORKSPACES);
  }
}
